import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Archive, ChevronLeft, Lock, Plus, Printer, Target, Thermometer, Trash2, Unlock } from 'lucide-react';
import { allItems, describeGoal, describeRange, FIX_BY_LABELS } from '@bp/shared';
import { api } from '@/lib/api';
import type { AuditDetail, ResponseRow } from '@/lib/types';
import { cn, fmtDate, fmtDateTime } from '@/lib/utils';
import { ActionStatusBadge, ModeBadge, ScoreBadge } from '@/components/badges';
import { Badge, Button, Card, CardHeader, Dialog, Empty, ErrorNote, Field, Loading, Stat, Textarea } from '@/components/ui';
import { LiveAudit } from '@/components/audit/LiveAudit';
import { MediaThumbs } from '@/components/audit/Media';
import { LaborResultsTable } from '@/components/audit/LaborPanel';
import { CreateTaskDialog } from '@/components/audit/CreateTaskDialog';

export function AuditPage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['audit', id], queryFn: () => api.get<AuditDetail>(`/api/audits/${id}`) });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const d = q.data!;
  if (d.can.edit) return <LiveAudit data={d} refetch={q.refetch} />;
  return <AuditResult data={d} refetch={q.refetch} />;
}

const resultStyle: Record<string, string> = { pass: 'text-pass', fail: 'text-fail', na: 'text-muted-fg', pending: 'text-warn' };
const resultLabel: Record<string, string> = { pass: 'Pass', fail: 'Fail', na: 'N/A', pending: 'Not answered' };

function AuditResult({ data, refetch }: { data: AuditDetail; refetch: () => Promise<unknown> }) {
  const a = data.audit;
  const content = data.template.content;
  const qc = useQueryClient();
  const nav = useNavigate();
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [taskFor, setTaskFor] = useState<ResponseRow | null>(null);
  const [showAll, setShowAll] = useState(false);
  const byKey = new Map(data.responses.map((r) => [r.item_key, r]));
  const failed = allItems(content).filter((i) => byKey.get(i.key)?.result === 'fail');
  const taskByResponse = new Map(data.action_items.map((t) => [t.response_id, t]));
  const rules = content.scoring_rules;
  const goalResults = a.status === 'completed' ? a.goal_results : data.live_goal_results;
  const sectionLabel = (k: string | null) => content.sections.find((s) => s.key === k)?.label;
  const mediaFor = (key: string) => data.media.filter((m) => m.item_key === key);

  async function archive() {
    try {
      await api.post(`/api/audits/${a.id}/archive`, { archived: !a.archived });
      toast.success(a.archived ? 'Audit restored.' : 'Audit archived. It no longer counts toward store metrics.');
      await qc.invalidateQueries({ queryKey: ['audits'] });
      await refetch();
    } catch (e) { toast.error((e as Error).message); }
  }
  async function remove() {
    if (!confirm('Delete this audit permanently, including photos? Action items stay but lose the link.')) return;
    try {
      await api.del(`/api/audits/${a.id}`);
      await qc.invalidateQueries({ queryKey: ['audits'] });
      nav(`/stores/${a.store_id}`);
    } catch (e) { toast.error((e as Error).message); }
  }

  return (
    <>
      <div className="mb-5">
        <Link to={`/stores/${a.store_id}`} className="mb-2 inline-flex items-center gap-1 text-sm text-muted-fg hover:text-fg"><ChevronLeft className="size-4" />{data.store.name}</Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold sm:text-2xl">{data.template.name}</h1>
              <ModeBadge mode={a.mode} />
              {a.locked && <Badge><Lock className="size-3" />Locked {fmtDateTime(a.locked_at)}</Badge>}
              {a.status === 'in_progress' && <Badge tone="warn">In progress</Badge>}
              {a.archived && <Badge tone="warn">Archived</Badge>}
            </div>
            <p className="mt-1 text-sm text-muted-fg">{fmtDate(a.audit_date)} · {a.auditor_name} · template v{data.template.version_no}{a.completed_at && ` · finished ${fmtDateTime(a.completed_at)}`}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/audits/${a.id}/print`} target="_blank"><Button variant="outline"><Printer className="size-4" />Print</Button></Link>
            {data.can.unlock && <Button variant="outline" onClick={() => setUnlockOpen(true)}><Unlock className="size-4" />Unlock</Button>}
            {data.can.archive && a.status === 'completed' && <Button variant="outline" onClick={archive}><Archive className="size-4" />{a.archived ? 'Restore' : 'Archive'}</Button>}
            {data.can.delete && <Button variant="ghost" className="text-fail" onClick={remove}><Trash2 className="size-4" /></Button>}
          </div>
        </div>
      </div>

      {a.status === 'in_progress' && <div className="mb-4 rounded-lg bg-warn-bg px-4 py-3 text-sm text-warn">This audit is still in progress by {a.auditor_name}. You're seeing it read-only.</div>}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Score" value={<ScoreBadge score={data.summary.score} passing={rules.passing_threshold} warning={rules.warning_threshold} className="px-3 py-1 text-xl" />} sub={a.mode === 'practice' ? 'Practice — not counted' : `Pass ≥ ${rules.passing_threshold}%, warning ≥ ${rules.warning_threshold}%`} />
        <Stat label="Passed" value={data.summary.passed} tone="pass" />
        <Stat label="Failed" value={data.summary.failed} tone={data.summary.failed ? 'fail' : 'neutral'} />
        <Stat label="N/A" value={data.summary.na} sub={`${data.summary.total_items} items`} />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title={`Failed items (${failed.length})`} subtitle={a.mode === 'official' ? 'Each failure gets an action item with an owner and due date.' : undefined} />
          {failed.length === 0 ? <Empty title="Nothing failed. Nice work." /> : (
            <ul className="divide-y">
              {failed.map((item) => {
                const r = byKey.get(item.key)!;
                const task = taskByResponse.get(r.id);
                return (
                  <li key={item.key} className="px-4 py-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium">{item.text}</p>
                        <p className="text-xs text-muted-fg">{sectionLabel(r.section_key)}{r.temperature !== null && ` · reading ${r.temperature}° (${describeRange(item)})`}{r.fix_by_window && ` · fix by ${FIX_BY_LABELS[r.fix_by_window]}`}{r.prior_failure_count > 0 && ` · failed ${r.prior_failure_count}× before`}</p>
                      </div>
                      {task ? (
                        <Link to={`/tasks/${task.id}`} className="flex items-center gap-2 text-sm"><ActionStatusBadge status={task.status} overdueDays={task.overdue_days} /><span className="text-muted-fg">{task.assignee_name || 'Unassigned'}</span></Link>
                      ) : data.can.create_action_items && a.status === 'completed' ? (
                        <Button size="sm" variant="outline" onClick={() => setTaskFor(r)}><Plus className="size-3.5" />Create task</Button>
                      ) : null}
                    </div>
                    {r.comment && <p className="mt-1.5 text-sm">“{r.comment}”</p>}
                    {mediaFor(item.key).length > 0 && <div className="mt-2"><MediaThumbs items={mediaFor(item.key)} size="sm" /></div>}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <div className="space-y-5">
          <Card>
            <CardHeader title="Sections" />
            <div className="space-y-2 p-4 text-sm">
              {content.sections.map((s) => {
                const v = data.summary.section_scores[s.key];
                return (
                  <div key={s.key} className="flex items-center gap-3">
                    <span className="w-36 truncate">{s.label}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted"><div className={cn('h-full', v >= rules.passing_threshold ? 'bg-pass' : v >= rules.warning_threshold ? 'bg-warn' : 'bg-fail')} style={{ width: `${v}%` }} /></div>
                    <span className="tabular w-10 text-right font-medium">{v}%</span>
                  </div>
                );
              })}
            </div>
          </Card>
          {goalResults.length > 0 && (
            <Card>
              <CardHeader title="Goals" />
              <ul className="divide-y text-sm">
                {goalResults.map((g) => (
                  <li key={g.goal_id} className="flex items-center justify-between gap-2 px-4 py-2.5">
                    <span className="flex items-center gap-2"><Target className="size-4 text-muted-fg" />{describeGoal(g, sectionLabel(g.section_key))}</span>
                    <Badge tone={g.met ? 'pass' : 'fail'}>{g.met ? 'Met' : 'Missed'} · {g.actual}{g.metric === 'max_failed_items' ? '' : '%'}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {content.labor_config.enabled && data.labor_results.correct_hours !== null && (
            <Card>
              <CardHeader title="Labor" />
              <LaborResultsTable r={data.labor_results} />
              {mediaFor('__labor').length > 0 && <div className="border-t p-4"><MediaThumbs items={mediaFor('__labor')} size="sm" /></div>}
            </Card>
          )}
          {a.notes && (
            <Card>
              <CardHeader title="Notes" />
              <p className="px-4 py-3 text-sm whitespace-pre-wrap">{a.notes}</p>
            </Card>
          )}
        </div>
      </div>

      <Card className="mt-5">
        <CardHeader title="All items" action={<Button size="sm" variant="ghost" onClick={() => setShowAll((s) => !s)}>{showAll ? 'Hide' : 'Show'}</Button>} />
        {showAll && content.sections.map((s) => (
          <div key={s.key}>
            <p className="bg-muted px-4 py-2 text-xs font-semibold tracking-wide text-muted-fg uppercase">{s.label} · {data.summary.section_scores[s.key]}%</p>
            <ul className="divide-y">
              {s.items.map((i) => {
                const r = byKey.get(i.key);
                return (
                  <li key={i.key} className="flex items-start justify-between gap-3 px-4 py-2 text-sm">
                    <div className="min-w-0">
                      <p>{i.text}</p>
                      {r?.comment && <p className="text-xs text-muted-fg">“{r.comment}”</p>}
                      {mediaFor(i.key).length > 0 && <div className="mt-1"><MediaThumbs items={mediaFor(i.key)} size="sm" /></div>}
                    </div>
                    <div className="flex-none text-right">
                      <span className={cn('font-medium', resultStyle[r?.result ?? 'pending'])}>{resultLabel[r?.result ?? 'pending']}</span>
                      {r?.temperature !== null && r?.temperature !== undefined && <p className="text-xs text-muted-fg"><Thermometer className="inline size-3" /> {r.temperature}°{i.temp_unit}</p>}
                      {r?.answered_at && <p className="text-xs text-muted-fg">{fmtDateTime(r.answered_at)}</p>}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </Card>

      {unlockOpen && <UnlockDialog auditId={a.id} open={unlockOpen} onOpenChange={setUnlockOpen} onDone={refetch} />}
      {taskFor && (
        <CreateTaskDialog
          auditId={a.id}
          auditDate={a.audit_date}
          storeId={a.store_id}
          response={taskFor}
          open={!!taskFor}
          onOpenChange={(o) => !o && setTaskFor(null)}
          onCreated={() => { void refetch(); void qc.invalidateQueries({ queryKey: ['tasks'] }); }}
        />
      )}
    </>
  );
}

function UnlockDialog({ auditId, open, onOpenChange, onDone }: { auditId: string; open: boolean; onOpenChange: (o: boolean) => void; onDone: () => Promise<unknown> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Unlock this audit?"
      description="The auditor can change answers again, then must finish it once more. The reason is kept in the activity log."
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button loading={busy} onClick={async () => {
        setBusy(true);
        try {
          await api.post(`/api/audits/${auditId}/unlock`, { reason });
          toast.success('Audit unlocked.');
          onOpenChange(false);
          await onDone();
        } catch (e) { setError(e); } finally { setBusy(false); }
      }}>Unlock</Button></>}
    >
      <Field label="Reason"><Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Item 12 was marked fail by mistake" /></Field>
      <div className="mt-3"><ErrorNote error={error} /></div>
    </Dialog>
  );
}
